import { useState } from "react";

import { makeGetApiCall, makePostApiCall } from "../../shared/makeApiCall";
import { printServerRenderedLabel } from "../../shared/localPrintAgent";
import { fetchUsersForDropdown } from "../../shared/usersForDropdown";
import { USER_TYPE_CODE } from "../../shared/userReferenceCodes";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import PackagingDetails from "../../components/forms/packaging/PackagingDetails";
import PackagingTransferModal from "../../components/packaging/PackagingTransferModal";
import FilterSingleSelectDropdown from "../../components/filterDropdown/FilterSingleSelectDropdown";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import { generateUniqueNumber } from "../../shared/utility";
import {
  buildGetPackagingSubItemsPayload,
  deleteMultiplePackaging,
  filterSelectableRows,
  getPackagingGroupKey,
  mapRowToBulkRecord,
  normalizePackagingSubItemRows,
  parsePackagingTransferResponse,
  transferPackagedItemByBarcode,
} from "../../shared/packagingBulkActions";
import {
  cleanFiltersForRequest,
  serializeMultiSelectFiltersForRequest,
} from "../../shared/filterUtils";

const reportTypeOptions = [{ value: 1, label: "Packaging Report" }];

const PACKAGING_KPI_ICONS = [
  "completed",
  "manufacturedWeight",
  "targetWeight",
  "inProgress",
];

function buildPackagingAnalyticsStats(data) {
  if (Array.isArray(data?.stats) && data.stats.length > 0) {
    return data.stats.map((item, index) => ({
      ...item,
      icon:
        item.icon ?? PACKAGING_KPI_ICONS[index % PACKAGING_KPI_ICONS.length],
    }));
  }

  return [];
}

export function PackagingRecordsViewModel(logout, addSnackbar) {
  const [filters, setFilters] = useState({
    product: [],
    category: [],
    brand: [],
    supervisor: [],
    employee1: [],
    startDate: null,
    endDate: null,
  });

  const [analytics, setAnalytics] = useState({
    range: null,
    stats: [],
    loading: true,
  });
  const [brands, setBrands] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [supervisorOptions, setSupervisorOptions] = useState([]);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };
  const [loading, setLoading] = useState(false);
  const [packagings, setPackagings] = useState({
    data: [],
  });
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [printingLabelType, setPrintingLabelType] = useState({ type: 2 });
  const [showFilters, setShowFilters] = useState(false);
  const [showDates, setShowDates] = useState(false);
  const [packagingLabel, setPackagingLabel] = useState({
    brand_image1: null,
    brand_image2: null,
    brand_image3: null,
    brand_name: null,
    address: null,
    lot_number: null,
    product_plu: null,
    ingredients: null,
    product_name: null,
    product_description: null,
    passed_from_metal_detector: null,
    product_weight: null,
    barcodeHtml: null,
    barcode: null,
    printCounter: 0,
    production_date: null,
    best_before_date: null,
  });
  const [bulkActionInProgress, setBulkActionInProgress] = useState({
    reprintAllLot: null,
    barcode: "",
    transfer: false,
    delete: false,
  });
  const [reportFilters, setReportFilters] = useState({
    type: { value: -1, label: "Select report" },
  });
  const [reportInProgress, setReportInProgress] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [subItemsLoadingByKey, setSubItemsLoadingByKey] = useState({});
  const [packagingSubItemsByKey, setPackagingSubItemsByKey] = useState({});
  const [palletExpandChildren, setPalletExpandChildren] = useState({});
  const [palletExpandLoading, setPalletExpandLoading] = useState({});
  const [productsForDropdown, setProductsForDropdown] = useState([]);
  const [productTypeOptions, setProductTypeOptions] = useState([]);

  const getAnalytics = async function (search, filterArg) {
    setAnalytics((prev) => ({ ...prev, loading: true }));
    const payload = {
      filters: serializeMultiSelectFiltersForRequest(
        cleanFiltersForRequest({ ...filterArg, search: search }),
      ),
    };
    let callResult = await makePostApiCall(`fetchPackagingAnalytics`, payload);
    if (callResult.success) {
      const data = callResult.data;
      setAnalytics({
        loading: false,
        range: data?.range ?? null,
        totalRecords: data?.totalRecords,
        stats: buildPackagingAnalyticsStats(data),
      });
    } else {
      setAnalytics((prev) => ({ ...prev, loading: false }));
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getPackagings = async function (search, page, filterArg) {
    setLoading(true);
    const payload = {
      page: page,
      filters: serializeMultiSelectFiltersForRequest(
        cleanFiltersForRequest({ ...filterArg, search: search }),
      ),
    };
    getAnalytics(search, filterArg);
    let callResult = await makePostApiCall(`fetchAllPackagingRecords`, payload);
    setLoading(false);
    if (callResult.success) {
      setPackagings(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getPackagingSubItems = async function (row) {
    const groupKey = getPackagingGroupKey(row);
    setSubItemsLoadingByKey((prev) => ({ ...prev, [groupKey]: true }));

    const payload = buildGetPackagingSubItemsPayload(row);

    let callResult = await makePostApiCall(`getPackagingSubItems`, payload);
    if (callResult.success) {
      const raw = Array.isArray(callResult.data)
        ? callResult.data
        : (callResult.data?.cartons ?? []);
      setPackagingSubItemsByKey((prev) => ({
        ...prev,
        [groupKey]: normalizePackagingSubItemRows(raw),
      }));
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    setSubItemsLoadingByKey((prev) => ({ ...prev, [groupKey]: false }));
  };

  const fetchPalletExpand = async function (barcode) {
    setPalletExpandLoading((prev) => ({ ...prev, [barcode]: true }));
    const path = `pallet/${encodeURIComponent(barcode)}/expand`;
    let callResult = await makeGetApiCall(path);
    if (callResult.success) {
      // API returns { cartons: [...] }
      const list = Array.isArray(callResult.data)
        ? callResult.data
        : (callResult.data?.cartons ?? []);
      setPalletExpandChildren((prev) => ({
        ...prev,
        [barcode]: normalizePackagingSubItemRows(list),
      }));
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    setPalletExpandLoading((prev) => ({ ...prev, [barcode]: false }));
  };

  const deletePackaging = async function (barcode, index) {
    setLoading(true);
    const payload = {
      shipping_barcode: barcode,
    };
    let callResult = await makePostApiCall(
      `deleteSinglePackagedItemByBarcode`,
      payload,
    );
    setLoading(false);
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });

      setPackagingSubItemsByKey((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          next[key] = (next[key] || []).filter(
            (item) => item.barcode !== barcode,
          );
        }
        return next;
      });
      setPalletExpandChildren((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(next)) {
          next[k] = (next[k] || []).filter((item) => item.barcode !== barcode);
        }
        return next;
      });
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const askDeletePackaging = function (packaging, index) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deletePackaging(packaging.barcode, index)}
          message={`Are you sure you want to delete ${packaging.product_name} - Serial: ${packaging.serial_number}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const deletePackagingLot = async function (lot, packaging_date) {
    setLoading(true);

    let callResult = await makePostApiCall(`deleteEntirePackaging/${lot}`, {
      packaging_date: packaging_date,
    });
    setLoading(false);
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      getPackagings(null, 1, filters);

      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const askDeletePackagingLot = function (packaging, index) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() =>
            deletePackagingLot(packaging.lot_number, packaging.packaging_date)
          }
          message={`Are you sure you want to delete ${packaging.product_name} - Lot: ${packaging.lot_number}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const refreshPackagingViews = async function (
    search,
    page,
    filterArg,
    expandedParents = [],
    expandedPalletBarcodes = [],
  ) {
    await getPackagings(search, page, filterArg);
    if (expandedParents.length > 0) {
      await Promise.all(
        expandedParents.map((parent) => getPackagingSubItems(parent)),
      );
    }
    if (expandedPalletBarcodes.length > 0) {
      await Promise.all(
        expandedPalletBarcodes.map((barcode) => fetchPalletExpand(barcode)),
      );
    }
  };

  const transferSelectedPackaging = async function (
    selectedRows,
    destination,
    refreshContext,
    onClearSelection,
  ) {
    const rows = filterSelectableRows(selectedRows);
    if (rows.length === 0) {
      showErrorModal(
        "Nothing to transfer",
        "Select in-stock cartons or pallets that are not on a shipment.",
      );
      return false;
    }

    setBulkActionInProgress((prev) => ({ ...prev, transfer: true }));

    const callResult = await transferPackagedItemByBarcode(
      rows.map((row) => row.barcode),
      destination,
    );

    setBulkActionInProgress((prev) => ({ ...prev, transfer: false }));
    setEmptyModelOpen({ open: false, childComponent: null });

    if (callResult.errorStatus === 401) {
      logout();
      return false;
    }

    if (callResult.success) {
      const { movedCount, failed } = parsePackagingTransferResponse(
        callResult.data,
      );

      if (movedCount > 0) {
        window.location.reload();
        return true;
      }

      const failureSummary = failed
        .slice(0, 3)
        .map((item) => `${item.barcode}: ${item.message}`)
        .join(" ");

      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          failureSummary || "Transfer failed.",
          SnackbarType.Error,
        ),
      );
      return false;
    }

    const failed = Array.isArray(callResult.data?.failed)
      ? callResult.data.failed
      : [];
    const failureSummary =
      failed.length > 0
        ? failed
            .slice(0, 3)
            .map((item) => `${item.barcode}: ${item.message}`)
            .join(" ")
        : (callResult.data?.message ??
          "Could not transfer selected packaging.");

    showErrorModal("Transfer failed", failureSummary);
    return false;
  };

  const deleteSelectedPackaging = async function (
    selectedRows,
    refreshContext,
    onClearSelection,
  ) {
    const rows = filterSelectableRows(selectedRows);
    if (rows.length === 0) {
      showErrorModal(
        "Nothing to delete",
        "Select in-stock cartons or pallets that are not on a shipment.",
      );
      return;
    }

    setBulkActionInProgress((prev) => ({ ...prev, delete: true }));

    const callResult = await deleteMultiplePackaging(
      rows.map((row) => mapRowToBulkRecord(row)),
    );

    setBulkActionInProgress((prev) => ({ ...prev, delete: false }));
    setEmptyModelOpen({ open: false, childComponent: null });

    if (callResult.success) {
      await refreshPackagingViews(
        refreshContext.search,
        refreshContext.page,
        refreshContext.filters,
        refreshContext.expandedParents ?? [],
        refreshContext.expandedPalletBarcodes ?? [],
      );
      onClearSelection?.();

      const notDeleted = callResult.data?.not_deleted_packaging_records ?? [];
      if (callResult.data?.not_deleted && notDeleted.length > 0) {
        const skipped = notDeleted
          .map((item) => item.barcode)
          .filter(Boolean)
          .join(", ");
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            skipped
              ? `Some items could not be deleted (on shipment): ${skipped}`
              : "Some selected items could not be deleted.",
            SnackbarType.Error,
          ),
        );
      } else {
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            callResult.data?.message ?? "Selected packaging deleted.",
            SnackbarType.Success,
          ),
        );
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showErrorModal(
          "Delete failed",
          callResult.data?.message ?? "Could not delete selected packaging.",
        );
      }
    }
  };

  const openTransferPackagingModal = function (
    selectedRows,
    refreshContext,
    onClearSelection,
  ) {
    const rows = filterSelectableRows(selectedRows);
    if (rows.length === 0) {
      showErrorModal(
        "Nothing to transfer",
        "Select in-stock cartons or pallets that are not on a shipment.",
      );
      return;
    }

    setEmptyModelOpen({
      childComponent: (
        <PackagingTransferModal
          selectedItems={rows.map((row) => mapRowToBulkRecord(row))}
          onCancel={() =>
            setEmptyModelOpen({ open: false, childComponent: null })
          }
          onConfirm={(destination) =>
            transferSelectedPackaging(
              selectedRows,
              destination,
              refreshContext,
              onClearSelection,
            )
          }
        />
      ),
      open: true,
    });
  };

  const askDeleteSelectedPackaging = function (
    selectedRows,
    refreshContext,
    onClearSelection,
  ) {
    const rows = filterSelectableRows(selectedRows);
    if (rows.length === 0) {
      showErrorModal(
        "Nothing to delete",
        "Select in-stock cartons or pallets that are not on a shipment.",
      );
      return;
    }

    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() =>
            deleteSelectedPackaging(
              selectedRows,
              refreshContext,
              onClearSelection,
            )
          }
          message={`Delete ${rows.length} packaging unit${rows.length === 1 ? "" : "s"}? This cannot be undone.`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const packagingReprintAll = async function (lot, packagingDate) {
    const payload = { packaging_date: packagingDate };
    setBulkActionInProgress({
      reprintAllLot: lot,
      barcode: "",
      transfer: false,
      delete: false,
    });
    let callResult = await makePostApiCall(
      `reprintPackagingByLot/${lot}`,
      payload,
    );

    if (callResult.success) {
      if (callResult.data.number_of_label_to_print > 1) {
        window.open(callResult.data.location, "_blank");
      } else {
        const item = callResult.data.items[0];
        setPackagingLabel({
          brand_image1: item.brand_image1,
          brand_image2: item.brand_image2,
          brand_image3: item.brand_image3,
          brand_name: item.brand_name,
          address: item.address,
          lot_number: item.lot_number,
          product_plu: item.product_plu,
          ingredients: item.ingredients,
          product_name: item.product_name,
          product_description: item.product_description,
          passed_from_metal_detector: item.passed_from_metal_detector,
          product_weight: item.product_weight + "kg",
          barcodeHtml: item.barcodeHtml,
          barcode: item.barcode,
          production_date: item.production_date,
          best_before_date: item.best_before_date,
          product_barcode: item.product_barcode,
          product_barcode_html: item.product_barcode_html,
        });
        // const labels = {
        //   items: callResult.data.items,
        //   printCounter: 1,
        // };
        setTimeout(function () {
          window.print();
          // printPackagingLabelRecursive(labels);
        }, 250);
      }
    }

    setBulkActionInProgress({
      reprintAllLot: null,
      barcode: "",
      transfer: false,
      delete: false,
    });
  };

  const reprintSinglePackagingLabel = async function (barcode) {
    setBulkActionInProgress({
      reprintAllLot: null,
      barcode: barcode,
      transfer: false,
      delete: false,
    });
    let callResult = await makePostApiCall(
      `reprintPackagingByBarcode/${barcode}`,
    );

    if (callResult.success) {
      const labelDetails = callResult.data?.label_details ?? {};
      const element =
        labelDetails.carton_labels?.[0] ?? labelDetails.pallet_label?.[0];

      if (!element) {
        showErrorModal(
          "Reprint failed",
          "Could not load label data for reprint.",
        );
      } else {
        const printResult = await printServerRenderedLabel(element);
        if (!printResult?.success) {
          const errorMessage =
            printResult?.data?.subtitle ??
            printResult?.data?.message ??
            printResult?.data?.title ??
            "Print failed. Please try again.";
          showErrorModal("Print error", errorMessage);
        }
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showErrorModal(
          "Reprint failed",
          callResult.data?.message ??
            callResult.data?.subtitle ??
            "Could not load label data for reprint.",
        );
      }
    }

    setBulkActionInProgress({
      reprintAllLot: null,
      barcode: "",
      transfer: false,
      delete: false,
    });
  };

  const openPackagingDetailsSideView = async function (lot_number, plu) {
    let callResult = await makePostApiCall(`fetchPackagingRecord`, {
      lot_number: lot_number,
      plu: plu,
    });
    if (callResult.success) {
      const packagedItems = Array.isArray(callResult.data)
        ? callResult.data
        : (callResult.data?.packaged_material ?? []);

      setSlideOver({
        childComponent: (
          <PackagingDetails
            packagedItem={packagedItems}
            setOpen={setSlideOver}
          />
        ),
        open: true,
      });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getUsersForDropdown = async function (type, department) {
    let callResult = await fetchUsersForDropdown(type, department);

    if (callResult.success) {
      if (type === USER_TYPE_CODE.REGULAR) {
        setEmployeeOptions(callResult.data);
      } else {
        setSupervisorOptions(callResult.data);
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getProductTypes = async function () {
    let callResult = await makePostApiCall(`productTypesForDropdown`);

    if (callResult.success) {
      setProductTypeOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const fetchBrandsForDropdown = async function () {
    let callResult = await makePostApiCall(`brandsForDropdown`);

    if (callResult.success) {
      setBrands(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const fetchProductsForDropdown = async function () {
    let callResult = await makePostApiCall(`fetchProductsForDropdown`);

    if (callResult.success) {
      setProductsForDropdown(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const resetFilters = () => {
    const clearedFilters = {
      product: [],
      category: [],
      brand: [],
      supervisor: [],
      employee1: [],
      startDate: null,
      endDate: null,
    };
    setFilters(clearedFilters);

    setShowFilters(false);
    getPackagings(null, 1, clearedFilters);
  };

  const generatePackagingReport = async function () {
    setReportInProgress(true);
    let callResult = await makePostApiCall(`generatePackagingReport`, {
      ...reportFilters,
      startDate: filters.startDate,
      endDate: filters.endDate,
    });
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    setReportInProgress(false);
  };

  const reportComponents = [
    <FilterSingleSelectDropdown
      header="Report type"
      onChange={(value) =>
        setReportFilters({
          ...reportFilters,
          type: value,
        })
      }
      options={reportTypeOptions}
      resetFilter={() =>
        setReportFilters({
          ...reportFilters,
          type: { value: -1, label: "Select report" },
        })
      }
      valueName="type"
      value={reportFilters.type}
    />,
  ];

  return {
    askDeletePackaging,
    emptyModal,
    setEmptyModelOpen,
    getPackagings,
    loading,
    packagings,
    setSlideOver,
    slideOver,
    packagingReprintAll,
    printingLabelType,
    packagingLabel,
    bulkActionInProgress,
    reprintSinglePackagingLabel,
    askDeletePackagingLot,
    openTransferPackagingModal,
    askDeleteSelectedPackaging,
    openPackagingDetailsSideView,
    fetchBrandsForDropdown,
    getUsersForDropdown,
    filters,
    showFilters,
    resetFilters,
    setShowFilters,
    generatePackagingReport,
    reportComponents,
    showReports,
    setShowReports,
    reportInProgress,
    analytics,
    getPackagingSubItems,
    fetchPalletExpand,
    packagingSubItemsByKey,
    subItemsLoadingByKey,
    palletExpandChildren,
    palletExpandLoading,
    fetchProductsForDropdown,
    getProductTypes,
    setShowDates,
    showDates,
    setFilters,
    brands,
    productsForDropdown,
    productTypeOptions,
    supervisorOptions,
    employeeOptions,
  };
}
